import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConventionRegistration } from 'app/models/convention-registration.model';
import { DailyRegister } from 'app/models/daily-register.model';
import { DeleteConventionRegistrationHistory } from 'app/models/delete-convention-registration-history.model';
import { EventConvention } from 'app/models/event-convention.model';
import { ProcessedEntityExtension } from 'app/models/extensions/processed-entity-extension.model';
import { LOVNotQualifyReason } from 'app/models/lov-not-qualify-reason.model';
import { LOVProcessedEntityType } from 'app/models/lov-processed-entity-type.model';
import { LOVProcessedStatus } from 'app/models/lov-processed-status.model';
import { Note } from 'app/models/note.model';
import { PCTProvinceGrouping } from 'app/models/p-c-t-province-grouping.model';
import { ProcessedEntityCredential } from 'app/models/processed-entity-credential.model';
import { ProcessedEntityMailingContact } from 'app/models/processed-entity-mailing-contact.model';
import { ProcessedEntityPayment } from 'app/models/processed-entity-payment.model';
import { MRMValidators } from '@mrm/core';

export class ProcessedEntity extends ProcessedEntityExtension {
  newPayment: ProcessedEntityPayment = null;
  latestDailyRegister: DailyRegister = null;
  computedPaymentBalance: number = 0;
  processedEntityDBID: number = 0;
  conventionDBID: number = 0;
  parentProcessedEntityDBID?: number = null;
  luisEntityDBID?: number = null;
  luisEntityParentDBID?: number = null;
  luisEntityContextDBID?: number = null;
  luisContactRoleDBID?: number = null;
  processedEntityTypeDBID?: number = null;
  processedEntityName: string = null;
  processedEntityNumber: string = null;
  pctProvinceGroupingDBID?: number = null;
  isActivePCT?: boolean = null;
  city: string = null;
  provinceDBID?: number = null;
  hasBringForwardFile?: boolean = null;
  bringForwardFileNoteDBID?: number = null;
  bringForwardFileLocation?: number = null;
  noteDBID?: number = null;
  numberOfUnits?: number = null;
  hasQualifyToReceive?: boolean = null;
  isQualifyToReceiveOverride?: boolean = null;
  qualifyToReceiveOverrideDT?: Date = null;
  qualifyToReceiveOverrideNoteDBID?: number = null;
  notQualifyReasonDBID?: number = null;
  notQualifyReasonLetterProcessedDT?: Date = null;
  notQualifyReasonProcessedStatusDBID?: number = null;
  hasQualifyToSit?: boolean = null;
  isQualifyToSitOverride?: boolean = null;
  qualifyToSitOverrideDT?: Date = null;
  qualifyToSitOverrideNoteDBID?: number = null;
  hasNotificationUnits: boolean = false;
  unitsNotificationProcessedDT?: Date = null;
  unitsNotificationProcessedStatusDBID: number = 0;
  isAddedAtConvention?: boolean = null;
  paymentAmountTotal?: number = null;
  paymentAmountApplied?: number = null;
  isConventionAssistFund: boolean = false;
  printCommunication: boolean = false;
  createdDT: Date = new Date();
  createdBy: string = null;
  modifiedDT: Date = new Date();
  modifiedBy: string = null;
  rowVersion: number = 0;
  contactEmail: string = null;
  isDefaultContactEmail: boolean = false;
  paymentBalance?: number = null;
  bringForwardFileNote: Note = null;
  convention: EventConvention = null;
  notQualifyReason: LOVNotQualifyReason = null;
  notQualifyReasonProcessedStatus: LOVProcessedStatus = null;
  note: Note = null;
  pctProvinceGrouping: PCTProvinceGrouping = null;
  parentProcessedEntity: ProcessedEntity = null;
  processedEntityType: LOVProcessedEntityType = null;
  qualifyToReceiveOverrideNote: Note = null;
  qualifyToSitOverrideNote: Note = null;
  conventionRegistrations: ConventionRegistration[] = [];
  dailyRegisters: DailyRegister[] = [];
  deleteConventionRegistrationHistories: DeleteConventionRegistrationHistory[] = [];
  inverseParentProcessedEntity: ProcessedEntity[] = [];
  processedEntityCredentials: ProcessedEntityCredential[] = [];
  processedEntityMailingContacts: ProcessedEntityMailingContact[] = [];
  processedEntityPayments: ProcessedEntityPayment[] = [];

  static getForm(disableAll: boolean = false, includeNav: string[] = []): FormGroup {
    const form = new FormGroup({
      computedPaymentBalance: new FormControl({ value: 0, disabled: disableAll }),
      processedEntityDBID: new FormControl({ value: 0, disabled: disableAll }),
      conventionDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      parentProcessedEntityDBID: new FormControl({ value: null, disabled: disableAll }),
      luisEntityDBID: new FormControl({ value: null, disabled: disableAll }),
      luisEntityParentDBID: new FormControl({ value: null, disabled: disableAll }),
      luisEntityContextDBID: new FormControl({ value: null, disabled: disableAll }),
      luisContactRoleDBID: new FormControl({ value: null, disabled: disableAll }),
      processedEntityTypeDBID: new FormControl({ value: null, disabled: disableAll }),
      processedEntityName: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(1000)]),
      processedEntityNumber: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(10)]),
      pctProvinceGroupingDBID: new FormControl({ value: null, disabled: disableAll }),
      isActivePCT: new FormControl({ value: null, disabled: disableAll }),
      city: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(50)]),
      provinceDBID: new FormControl({ value: null, disabled: disableAll }),
      hasBringForwardFile: new FormControl({ value: null, disabled: disableAll }),
      bringForwardFileNoteDBID: new FormControl({ value: null, disabled: disableAll }),
      bringForwardFileLocation: new FormControl({ value: null, disabled: disableAll }),
      noteDBID: new FormControl({ value: null, disabled: disableAll }),
      numberOfUnits: new FormControl({ value: null, disabled: disableAll }),
      hasQualifyToReceive: new FormControl({ value: null, disabled: disableAll }),
      isQualifyToReceiveOverride: new FormControl({ value: null, disabled: disableAll }),
      qualifyToReceiveOverrideDT: new FormControl({ value: null, disabled: disableAll }),
      qualifyToReceiveOverrideNoteDBID: new FormControl({ value: null, disabled: disableAll }),
      notQualifyReasonDBID: new FormControl({ value: null, disabled: disableAll }),
      notQualifyReasonLetterProcessedDT: new FormControl({ value: null, disabled: disableAll }),
      notQualifyReasonProcessedStatusDBID: new FormControl({ value: null, disabled: disableAll }),
      hasQualifyToSit: new FormControl({ value: null, disabled: disableAll }),
      isQualifyToSitOverride: new FormControl({ value: null, disabled: disableAll }),
      qualifyToSitOverrideDT: new FormControl({ value: null, disabled: disableAll }),
      qualifyToSitOverrideNoteDBID: new FormControl({ value: null, disabled: disableAll }),
      hasNotificationUnits: new FormControl({ value: false, disabled: disableAll }, [Validators.required]),
      unitsNotificationProcessedDT: new FormControl({ value: null, disabled: disableAll }),
      unitsNotificationProcessedStatusDBID: new FormControl({ value: 0, disabled: disableAll }, [Validators.required, MRMValidators.number]),
      isAddedAtConvention: new FormControl({ value: null, disabled: disableAll }),
      paymentAmountTotal: new FormControl({ value: null, disabled: disableAll }),
      paymentAmountApplied: new FormControl({ value: null, disabled: disableAll }),
      isConventionAssistFund: new FormControl({ value: false, disabled: disableAll }, [Validators.required]),
      printCommunication: new FormControl({ value: false, disabled: disableAll }, [Validators.required]),
      createdDT: new FormControl({ value: new Date(), disabled: disableAll }, [Validators.required]),
      createdBy: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(50)]),
      modifiedDT: new FormControl({ value: new Date(), disabled: disableAll }, [Validators.required]),
      modifiedBy: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(50)]),
      rowVersion: new FormControl({ value: 0, disabled: disableAll }),
      contactEmail: new FormControl({ value: null, disabled: disableAll }, [Validators.maxLength(256)]),
      isDefaultContactEmail: new FormControl({ value: false, disabled: disableAll }, [Validators.required]),
      paymentBalance: new FormControl({ value: null, disabled: disableAll }),
      conventionRegistrations: new FormArray([]),
      dailyRegisters: new FormArray([]),
      deleteConventionRegistrationHistories: new FormArray([]),
      inverseParentProcessedEntity: new FormArray([]),
      processedEntityCredentials: new FormArray([]),
      processedEntityMailingContacts: new FormArray([]),
      processedEntityPayments: new FormArray([])
    });

    // Only add navigational properties to the form when needed.
    // If navigational properties are included they MUST NOT be null when patching the form.
    if (includeNav.length > 0) {
      for (const key of includeNav) {
        switch (key.toLowerCase()) {
          case 'newpayment':
            form.addControl('newPayment', ProcessedEntityPayment.getForm(disableAll)); break;
          case 'latestdailyregister':
            form.addControl('latestDailyRegister', DailyRegister.getForm(disableAll)); break;
          case 'bringforwardfilenote':
            form.addControl('bringForwardFileNote', Note.getForm(disableAll)); break;
          case 'convention':
            form.addControl('convention', EventConvention.getForm(disableAll)); break;
          case 'notqualifyreason':
            form.addControl('notQualifyReason', LOVNotQualifyReason.getForm(disableAll)); break;
          case 'notqualifyreasonprocessedstatus':
            form.addControl('notQualifyReasonProcessedStatus', LOVProcessedStatus.getForm(disableAll)); break;
          case 'note':
            form.addControl('note', Note.getForm(disableAll)); break;
          case 'pctprovincegrouping':
            form.addControl('pctProvinceGrouping', PCTProvinceGrouping.getForm(disableAll)); break;
          case 'parentprocessedentity':
            form.addControl('parentProcessedEntity', ProcessedEntity.getForm(disableAll)); break;
          case 'processedentitytype':
            form.addControl('processedEntityType', LOVProcessedEntityType.getForm(disableAll)); break;
          case 'qualifytoreceiveoverridenote':
            form.addControl('qualifyToReceiveOverrideNote', Note.getForm(disableAll)); break;
          case 'qualifytositoverridenote':
            form.addControl('qualifyToSitOverrideNote', Note.getForm(disableAll)); break;
          default:
            // tslint:disable-next-line:max-line-length
            throw new Error(`'${key}' is not a property of ProcessedEntity. Please use one of the following navigational property: 'newPayment' | 'latestDailyRegister' | 'bringForwardFileNote' | 'convention' | 'notQualifyReason' | 'notQualifyReasonProcessedStatus' | 'note' | 'pctProvinceGrouping' | 'parentProcessedEntity' | 'processedEntityType' | 'qualifyToReceiveOverrideNote' | 'qualifyToSitOverrideNote'`);
        }
      }
    }

    return form;
  }
}